import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react";

type ThemeMode = "light" | "dark" | "system";
type TypeScale = 0.9 | 1 | 1.1 | 1.25;

type ThemeContextValue = {
  mode: ThemeMode;
  resolvedMode: "light" | "dark";
  setMode: (mode: ThemeMode) => void;
  typeScale: TypeScale;
  setTypeScale: (scale: TypeScale) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = "sf_theme";

type StoredTheme = { mode: ThemeMode; typeScale: TypeScale };

function readStored(): StoredTheme {
  if (typeof window === "undefined") return { mode: "system", typeScale: 1 };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { mode: "system", typeScale: 1 };
    const parsed = JSON.parse(raw) as Partial<StoredTheme>;
    const mode: ThemeMode =
      parsed.mode === "light" || parsed.mode === "dark" ? parsed.mode : "system";
    const typeScale: TypeScale =
      parsed.typeScale === 0.9 ||
      parsed.typeScale === 1.1 ||
      parsed.typeScale === 1.25 ||
      parsed.typeScale === 1
        ? parsed.typeScale
        : 1;
    return { mode, typeScale };
  } catch {
    return { mode: "system", typeScale: 1 };
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useState<StoredTheme>(() => readStored());
  const [systemDark, setSystemDark] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const resolvedMode: "light" | "dark" =
    stored.mode === "system" ? (systemDark ? "dark" : "light") : stored.mode;

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.classList.toggle("dark", resolvedMode === "dark");
    document.documentElement.style.setProperty("--type-scale", String(stored.typeScale));
  }, [resolvedMode, stored.typeScale]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  }, [stored]);

  const setMode = useCallback((mode: ThemeMode) => setStored((s) => ({ ...s, mode })), []);
  const setTypeScale = useCallback(
    (typeScale: TypeScale) => setStored((s) => ({ ...s, typeScale })),
    [],
  );

  return (
    <ThemeContext.Provider
      value={{
        mode: stored.mode,
        resolvedMode,
        setMode,
        typeScale: stored.typeScale,
        setTypeScale,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}

export type { ThemeMode, TypeScale };
